import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { FallbackComponent } from './fallback.component';
import { LottieModule } from 'ngx-lottie';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { SharedComponentsModule } from './shared/components/shared-components.module';
import { LogoutListenerComponent } from './logout-listener.component';
import { UnsubscribePageComponent } from './pages/unsubscribe/unsubscribe.component';
import { environment } from 'src/environments/environment';
import { LpTelegramPageComponent } from './pages/lp-telegram/lp-telegram.component';
import { WelcomeRelateduserComponent } from './pages/welcome-relateduser/welcome-relateduser.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { AccountDisabledComponent } from './pages/account-disabled/account-disabled.component';

import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService, MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, tap, map, take } from 'rxjs/operators';
import { ApiService } from './core/services/api.service';
import { TalentOnboardInvitationComponent } from './pages/talent-onboard-invitation/talent-onboard-invitation.component';
import { HmOnboardInvitationComponent } from './pages/hm-onboard-invitation/hm-onboard-invitation.component';
import { EmailPolicyBreakComponent } from './pages/email-policy-break/email-policy-break.component';
import { AuthorizedScopeComponent } from './pages/authorized-scope/authorized-scope.component';
import { InvitationExpiredComponent } from './pages/invitation-expired/invitation-expired.component';

export class CustomTranslateLoader implements TranslateLoader {
  private readonly CACHE_PREFIX = 'atalef_translations_';
  private readonly CACHE_VERSION_KEY = 'atalef_translations_version';
  static enTranslations: any = null;

  constructor(private http: HttpClient, private apiService: ApiService) {}

  getTranslation(lang: string): Observable<any> {
    // Always load en.json as fallback base (needed for MissingTranslationHandler)
    const enJsonRequest = this.http.get(`./assets/i18n/en.json`).pipe(
      tap(enJson => {
        // Store en.json for missing translation handler (static for global access)
        if (!CustomTranslateLoader.enTranslations) {
          CustomTranslateLoader.enTranslations = enJson;
        }
      })
    );

    // Check sessionStorage cache first
    const cachedTranslations = this.getCachedTranslations(lang);
    if (cachedTranslations) {
      console.log(`Loading translations for ${lang} from cache`);
      // Merge cache with en.json to ensure all keys are present
      return enJsonRequest.pipe(
        map(enJson => {
          const merged = this.deepMerge(enJson, cachedTranslations);
          console.log(`Merged cache with en.json for ${lang}`);
          return merged;
        })
      );
    }

    // Load from API endpoint using ApiService
    console.log(`Loading translations for ${lang} from API`);
    const apiRequest = this.apiService.getTranslationResources(lang).pipe(
      catchError(error => {
        console.error(`Failed to load translations for ${lang} from API`, error);
        return of({}); // Return empty object if API fails
      })
    );

    // Merge API translations with en.json fallback
    return forkJoin([enJsonRequest, apiRequest]).pipe(
      map(([enJson, apiTranslations]) => {
        // Deep merge: API translations override en.json
        const merged = this.deepMerge(enJson, apiTranslations);
        console.log(`Merged translations for ${lang}: ${Object.keys(apiTranslations).length} from API, ${Object.keys(enJson).length} from en.json`);
        
        // Cache the merged translations
        this.cacheTranslations(lang, merged);
        return merged;
      }),
      catchError(error => {
        console.error(`Failed to load translations for ${lang}, using en.json only`, error);
        // Final fallback to en.json only
        return this.http.get(`./assets/i18n/${lang}.json`);
      })
    );
  }

  private deepMerge(target: any, source: any, path: string[] = []): any {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        const nextPath = [...path, key];
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = this.deepMerge(target[key], source[key], nextPath);
          }
        } else if (this.shouldUseSourceValue(source[key], target?.[key], nextPath)) {
          output[key] = source[key];
        }
      });
    }
    
    return output;
  }

  private shouldUseSourceValue(sourceValue: any, targetValue: any, path: string[]): boolean {
    if (sourceValue === null || sourceValue === undefined) {
      return false;
    }

    if (typeof sourceValue !== 'string') {
      return true;
    }

    const normalizedSourceValue = sourceValue.trim();
    if (!normalizedSourceValue) {
      return false;
    }

    if (typeof targetValue !== 'string') {
      return true;
    }

    const localKey = path[path.length - 1];
    const fullKey = path.join('.');
    const unwrappedSourceValue = normalizedSourceValue.replace(/^\[(.*)\]$/, '$1').trim();
    const isPlaceholderValue = unwrappedSourceValue === localKey || unwrappedSourceValue === fullKey;

    return !isPlaceholderValue || !targetValue.trim();
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  private getCachedTranslations(lang: string): any | null {
    try {
      const cached = sessionStorage.getItem(this.CACHE_PREFIX + lang);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error reading from sessionStorage cache', error);
    }
    return null;
  }

  private cacheTranslations(lang: string, translations: any): void {
    try {
      sessionStorage.setItem(this.CACHE_PREFIX + lang, JSON.stringify(translations));
      sessionStorage.setItem(this.CACHE_VERSION_KEY, new Date().getTime().toString());
    } catch (error) {
      console.error('Error writing to sessionStorage cache', error);
    }
  }
}

export function HttpLoaderFactory(http: HttpClient, apiService: ApiService): TranslateLoader {
  return new CustomTranslateLoader(http, apiService);
}

// Custom handler for missing translations
export class CustomMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams): string {
    const key = params.key;
    
    // Try to get the value from en.json fallback
    const fallbackValue = this.getNestedValue(CustomTranslateLoader.enTranslations, key);
    
    if (fallbackValue) {
      console.log(`Using en.json fallback for missing key: ${key}`);
      return fallbackValue;
    }
    
    // In debug mode, show the key so developers can identify missing translations
    if (environment['debugResources']) {
      console.warn(`Missing translation for key: ${key} (not found in en.json either)`);
      return `[${key}]`; // Show key in brackets
    }
    
    // In production/release mode, return the last part of the key as fallback
    console.warn(`Missing translation for key: ${key}, using key as fallback`);
    const parts = key.split('.');
    return parts[parts.length - 1];
  }
  
  private getNestedValue(obj: any, key: string): string | null {
    if (!obj) return null;
    
    const parts = key.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return null;
      }
    }
    
    return typeof current === 'string' ? current : null;
  }
}

// Preload translations before app starts to prevent showing raw keys
export function preloadTranslations(translate: TranslateService): () => Promise<any> {
  return () => {
    const savedLanguage = localStorage.getItem('atalef_language') || 'en';
    translate.setDefaultLang('en');
    
    // Set document direction immediately based on saved language
    const RTL_LANGUAGES = new Set(['he', 'ar', 'fa', 'ur']);
    const isRtl = RTL_LANGUAGES.has(savedLanguage.toLowerCase());
    const dirValue = isRtl ? 'rtl' : 'ltr';
    
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', dirValue);
      document.documentElement.classList.remove('rtl', 'ltr');
      document.documentElement.classList.add(dirValue);
      
      if (document.body) {
        document.body.setAttribute('dir', dirValue);
        document.body.classList.remove('rtl', 'ltr');
        document.body.classList.add(dirValue);
      }
    }
    
    return translate.use(savedLanguage).toPromise();
  };
}

export function playerFactory(): any {  
  return import('lottie-web');
}

@NgModule({
  declarations: [
    AppComponent,
    FallbackComponent,
    LogoutListenerComponent,
    UnsubscribePageComponent,
    LpTelegramPageComponent,
    WelcomeRelateduserComponent,
    TalentOnboardInvitationComponent,
    HmOnboardInvitationComponent,
    UnauthorizedComponent,
    AccountDisabledComponent,
    EmailPolicyBreakComponent,
    AuthorizedScopeComponent,
    InvitationExpiredComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    LottieModule.forRoot({ player: playerFactory }),
    NgxGoogleAnalyticsModule.forRoot(environment.googleGA4Key),
    NgxGoogleAnalyticsRouterModule,
    AppRoutingModule,
    SharedComponentsModule,
    CoreModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, ApiService]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler
      },
      useDefaultLang: true
    })
  ],
  providers: [
    // provideClientHydration(),
    {
      provide: APP_INITIALIZER,
      useFactory: preloadTranslations,
      deps: [TranslateService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  // Translation is now preloaded via APP_INITIALIZER
}
